/* ==========================================================================
   Go-Live Modal
   Task F36: Confirm marking portal as go-live ready
   ========================================================================== */

import { useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Alert } from '@/components/ui/Alert'
import { markGoLive, type IntakeDetail } from '@/api/internal'
import { ApiClientError } from '@/api/client'

interface GoLiveModalProps {
  open: boolean
  onClose: () => void
  intake: IntakeDetail
  onSuccess: () => void
}

export function GoLiveModal({
  open,
  onClose,
  intake,
  onSuccess,
}: GoLiveModalProps) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string>()

  const handleGoLive = async () => {
    setLoading(true)
    setError(undefined)

    try {
      await markGoLive(intake.id)
      onSuccess()
      onClose()
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.getUserMessage())
      } else {
        setError('An unexpected error occurred')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Mark Go-Live Ready" size="md">
      {error && (
        <Alert variant="error" className="mb-4">
          {error}
        </Alert>
      )}

      <p className="text-sm text-charcoal mb-2">
        Mark this portal as go-live ready? No email will be sent yet.
      </p>

      <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-6">
        <p className="text-sm text-amber-800">
          Make sure catalog, settings, and branding are configured before proceeding.
        </p>
      </div>

      <div className="flex gap-3">
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={loading}
          className="flex-1"
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleGoLive}
          loading={loading}
          disabled={loading}
          className="flex-1"
        >
          Mark Go-Live
        </Button>
      </div>
    </Modal>
  )
}

export default GoLiveModal
